import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Tag, Typography, Button, Row, Col, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { ArrowLeftOutlined } from '@ant-design/icons';

import { getExpandableToursData } from 'constants/table';
import { useGetTourDetail } from 'hooks/useGetTourDetail';
import ExportDetailGroup from 'components/common/export-detail-group';
import UpdateTourStatusModal from 'components/tour/update-tour-status';
import useModalStore from 'stores/useModal';
import './detail-tour.scss';

export interface TourDataDetails {
  key: string;
  bookingId: number;
  branchName: string;
  travelDate: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  guestType: string;
}

function DetailTour() {
  const { t } = useTranslation();
  const { tourId } = useParams<{ tourId: string }>();
  const navigate = useNavigate();
  const { setIsOpenUpdateTourStatus } = useModalStore();

  const { data, isLoading } = useGetTourDetail(parseInt(tourId || '0'));

  // Transform the data to get the tour information
  const tourData = data ? Object.values(data)[0] : null;
  const tourName = data ? Object.keys(data)[0] : '';

  const tourDataDetails: TourDataDetails[] =
    tourData?.bookings?.flatMap(
      (booking: any) =>
        booking.customer_info?.map((customer: any, customerIndex: number) => ({
          key: `${tourId}-${booking.booking_id}-${customerIndex}`,
          bookingId: booking.booking_id,
          branchName: booking.branch_name,
          travelDate: booking.travel_date,
          customerName: customer?.name || '',
          customerPhone: customer?.phone || '',
          customerEmail: customer?.email || '',
          note: customer?.note || '',
          guestType: customer?.is_adult ? 'adult' : customer?.is_child ? 'child' : ''
        })) || []
    ) || [];

  const expandedColumns = getExpandableToursData(t);

  const handleBack = () => {
    navigate('/tour');
  };

  const handleUpdateTourStatus = () => {
    setIsOpenUpdateTourStatus(true);
  };

  if (isLoading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!tourData) {
    return <div>Tour not found</div>;
  }

  return (
    <div className="detail-tour-page">
      <div style={{ marginBottom: '24px' }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          className="back-button"
        >
          {t('common.actions.back')}
        </Button>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}
      >
        <Typography.Title level={2} className="page-title" style={{ margin: 0 }}>
          {t('tour.tourDetails')}
        </Typography.Title>
        <ExportDetailGroup
          fileName={`tour-detail-${tourId}`}
          dataToExport={tourDataDetails}
          tourId={tourId || ''}
          tourName={tourName}
          tourData={tourData}
          t={t}
        />
      </div>

      <Card
        className="info-card"
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography.Title level={4} className="card-title" style={{ margin: 0 }}>
              {t('tour.tourInformation')}
            </Typography.Title>
            <Button
              type="primary"
              className="ant-btn-secondary"
              onClick={handleUpdateTourStatus}
              style={{ height: '32px' }}
              disabled={tourData.tour_status == 'complete'}
            >
              {t('tour.updateTourStatus')}
            </Button>
          </div>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={6}>
            <div className="info-field">
              <Typography.Text strong className="field-label">
                {t('tour.tourName')}
              </Typography.Text>
              <div className="field-value">{tourName}</div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="info-field">
              <Typography.Text strong className="field-label">
                {t('tour.tourType')}
              </Typography.Text>
              <div style={{ marginTop: '8px' }}>
                <Tag
                  color={tourData?.is_single_tour ? 'blue' : 'green'}
                  style={{
                    fontSize: '14px',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontWeight: 500
                  }}
                >
                  {tourData?.is_single_tour ? t('tour.singleTour') : t('tour.tourPackage')}
                </Tag>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="info-field">
              <Typography.Text strong className="field-label">
                {t('tour.totalCustomers')}
              </Typography.Text>
              <div className="field-value">{tourData?.total_customers}</div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="info-field">
              <Typography.Text strong className="field-label">
                {t('tour.status')}
              </Typography.Text>
              <div style={{ marginTop: '8px' }}>
                <Tag
                  color={
                    tourData?.tour_status === 'confirm'
                      ? 'default'
                      : tourData?.tour_status === 'complete'
                        ? 'orange'
                        : tourData?.tour_status === 'cancel'
                          ? 'red'
                          : 'default'
                  }
                  style={{
                    fontSize: '14px',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontWeight: 500
                  }}
                >
                  {tourData?.tour_status ? t(`tour.tourStatus.${tourData.tour_status}`) : '-'}
                </Tag>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      <Card
        title={
          <Typography.Title level={4} className="card-title">
            {t('tour.customers')}
          </Typography.Title>
        }
        className="customers-card"
      >
        <Table
          columns={expandedColumns}
          dataSource={tourDataDetails}
          pagination={false}
          size="small"
          bordered
          locale={{
            emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
          }}
        />
      </Card>
      <UpdateTourStatusModal tourId={parseInt(tourId || '0')} />
    </div>
  );
}

export default DetailTour;
